import {
  useWSSnackbar,
  WSActions,
  WSDivider,
  WSForm,
  WSInfoBox,
  WSInputText,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSelect
} from "@wingspanhq/fe-component-library";
import {
  EngagementStatus,
  Payee,
  PayeeEngagementType
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import * as Yup from "yup";
import { ErrorAlert } from "../../../../v3/components/ErrorAlert";
import { queriesEngagements } from "@query";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import {
  useQueryIntegrationsQuickbooks,
  useQueryIntegrationsQuickbooksAccountExpenses,
  useQueryIntegrationsQuickbooksVendors
} from "../../../../query/integrations/queries";
import { WSQueries } from "../../../../query/WSQuery";
import { validatorEmail } from "../../../../shared/validators/validatorEmail";
import {
  contactCompanyValidator,
  contactNameValidator
} from "../../../../utils/validators";
import { selectorEngagementOptions } from "../../../EngagementTypes/selectors/selectorEngagementOptions";
import {
  FormPartialPayeeQboMapping,
  PAYEE_QBO_MAPPING_FORM_DEFAULT_VALUES,
  PayeeQboMappingFormValues
} from "../../../Integrations/components/FormPartialPayeeQboMapping";
import { selectorIsPayeeQboMappingEnabled } from "../../../Integrations/selectors/selectorIsPayeeQboMappingEnabled";
import { useCreateContractorBulkAction } from "../../hooks/useCreateContractorBulkAction";
import { CreatePayeeInput, useCreatePayee } from "../../hooks/useCreatePayee";
import { routeHelpersPayeeDetails } from "../../utils";

export type CreateContractorModalProps = WSModalCustomProps<Payee | void> & {
  onSuccess?: (client?: Payee) => Promise<void>;
  redirectToDetails?: boolean;
};

type FormData = Partial<PayeeQboMappingFormValues> & {
  company: string;
  // customFields?: ICustomFields; // TODO(v3): custom fields disabled — GET /v3/platform/custom-fields returns 501
  email: string;
  engagementIds?: string[];
  externalId?: string;
  name: string;
};

export const CreateContractorModal: React.FC<CreateContractorModalProps> = ({
  onClose,
  onSuccess,
  redirectToDetails
}) => {
  const { openSnackbar } = useWSSnackbar();

  const goToPayeeDetails = routeHelpersPayeeDetails.useGoTo();

  const queryEngagements = queriesEngagements.listEngagements.useQueryAll(
    { filter: { status: { anyOf: [EngagementStatus.ACTIVE] } } },
    { refetchOnMount: false, retry: false }
  );

  const [createPayee, createPayeeMeta] = useCreatePayee();
  const runCreateContractorBulkAction = useCreateContractorBulkAction();

  const queryFeatureFlags = useFeatureFlags();
  const queryIntegrationsQuickbooks = useQueryIntegrationsQuickbooks();
  const queryIntegrationsQuickbooksVendors =
    useQueryIntegrationsQuickbooksVendors();
  const queryIntegrationsQuickbooksExpenseAccounts =
    useQueryIntegrationsQuickbooksAccountExpenses();

  const isQboMappingEnabled = selectorIsPayeeQboMappingEnabled({
    expenseAccounts: queryIntegrationsQuickbooksExpenseAccounts.data,
    featureFlags: queryFeatureFlags?.data,
    qboService: queryIntegrationsQuickbooks.data,
    vendors: queryIntegrationsQuickbooksVendors.data
  });

  // TODO(v3): custom fields disabled — the v3 definitions read
  // (GET /v3/platform/custom-fields) returns 501. Re-enable the dynamic
  // custom-field rendering (definitions read + validation + defaults + values
  // write) once the v3 endpoint is implemented.

  return (
    <WSModal title="Add contractor" size="S" onClose={onClose}>
      <WSQueries
        queries={{
          queryEngagements
        }}
      >
        {() => {
          const engagements = (queryEngagements.flatData ?? []).filter(
            e => !e.isDefault
          );
          const engagementOptions = selectorEngagementOptions(engagements);
          return (
            <WSForm<FormData>
              defaultValues={{
                ...PAYEE_QBO_MAPPING_FORM_DEFAULT_VALUES,
                company: "",
                email: "",
                engagementIds: [] as string[],
                externalId: "",
                name: ""
              }}
              validationSchema={Yup.object().shape({
                company: contactCompanyValidator,
                email: validatorEmail.required("Required"),
                name: contactNameValidator
              })}
              onSubmit={async data => {
                const selectedEngagementIds = data.engagementIds || [];
                const isSingleSelection = selectedEngagementIds.length <= 1;

                // Payer-owned data (QBO mapping) has no home on v3 `PayeeCreate`;
                // it's bridged through `updatePayee` inside `useCreatePayee`. Skip
                // it when empty. (Custom fields disabled — v3 read is 501.)
                const payerOwnedData: CreatePayeeInput["payerOwnedData"] = {};

                if (isSingleSelection) {
                  if (
                    isQboMappingEnabled &&
                    (data.qboVendorId || data.qboExpenseAccountId)
                  ) {
                    payerOwnedData.integration = {
                      quickbooks: {
                        expenseAccountId: data.qboExpenseAccountId,
                        vendorId: data.qboVendorId
                      }
                    };
                  }

                  const result = await createPayee({
                    email: data.email,
                    name: data.name,
                    company: data.company,
                    externalId: data.externalId,
                    payerOwnedData,
                    engagements: selectedEngagementIds.map(id => ({
                      id,
                      engagementType:
                        engagements.find(e => e.id === id)?.engagementType ??
                        PayeeEngagementType.CONTRACTOR
                    }))
                  });

                  if (!result) return;
                  await onSuccess?.(result);
                  onClose(result);
                  redirectToDetails &&
                    goToPayeeDetails({ payeeId: result.id! });
                  openSnackbar({
                    message: "Contractor added successfully",
                    type: "success"
                  });
                } else {
                  const result = await runCreateContractorBulkAction(
                    {
                      payeeCreationData: {
                        email: data.email,
                        name: data.name,
                        company: data.company,
                        externalId: data.externalId,
                        payerOwnedData
                      },
                      engagements: selectedEngagementIds.map(id => ({
                        id,
                        name:
                          engagementOptions.find(o => o.value === id)?.label ||
                          id,
                        engagementType:
                          engagements.find(e => e.id === id)?.engagementType ??
                          PayeeEngagementType.CONTRACTOR
                      }))
                    },
                    {
                      toDetailsOnSuccess: !!redirectToDetails
                    }
                  );

                  if (!result.payee) return;
                  await onSuccess?.(result.payee);
                  onClose(result.payee);
                  result.toDetails &&
                    goToPayeeDetails({ payeeId: result.payee.id! });
                }
              }}
            >
              <WSList gap="L">
                <WSInfoBox>
                  After adding a contractor, they will be invited by the email
                  provided to sign-up for a Wingspan account in order to receive
                  payments from you.
                </WSInfoBox>

                <WSForm.Field
                  name="email"
                  label="Contractor email *"
                  component={WSInputText}
                  componentProps={{
                    helperText: "Contractor’s personal or business email",
                    tooltip: {
                      dark: true,
                      title: "Contractor email (required)",
                      children: `Send an invite to the contractor’s personal email or the email of the contractor’s business that they operate under.

We do not recommend using emails provided by your company since the contractor’s account is portable for their own use as a contractor.`
                    },
                    placeholder: "Contractor email"
                  }}
                />
                <WSForm.Field
                  name="company"
                  label="Contractor company"
                  component={WSInputText}
                  componentProps={{
                    tooltip: {
                      dark: true,
                      title: "Contractor company (optional)",
                      children: `If the contractor operates under their own contractor business name, enter it here. This information will be confirmed by the contractor when they onboard.`
                    },
                    placeholder: "Contractor company"
                  }}
                />
                <WSForm.Field
                  name="name"
                  label="Contractor full name"
                  component={WSInputText}
                  componentProps={{
                    tooltip: {
                      dark: true,
                      title: "Contractor full name (optional)",
                      children: `This information will be confirmed by the contractor when they onboard.`
                    },
                    placeholder: "Contractor full name"
                  }}
                />
                {/* TODO(v3): custom fields disabled — GET /v3/platform/custom-fields
                    returns 501. Restore the dynamic custom-field inputs here once
                    the v3 endpoint is implemented. */}
                <WSDivider my="S" />
                <WSForm.Field
                  name="externalId"
                  label="External ID"
                  component={WSInputText}
                  componentProps={{
                    placeholder: "External ID"
                  }}
                />
                {engagementOptions.length > 0 && (
                  <>
                    <WSDivider my="S" />
                    <WSForm.Field
                      name="engagementIds"
                      label="Engagements"
                      component={WSSelect}
                      componentProps={{
                        mode: "multiple",
                        placeholder: "Select engagements",
                        tooltip: {
                          dark: true,
                          title: "Engagements (optional)",
                          children: `An engagement is a type of work arrangement that allows payers to organize their diverse work relationships in a flexible way. You can assign one or more engagements when adding a new contractor.

If you have not set up engagements or do not plan to use engagements, you can skip this field. Engagements can be updated in the Contractors section of the app at any time.`
                        },
                        options: engagementOptions
                      }}
                    />
                  </>
                )}

                <WSForm.Value name="engagementIds">
                  {(engagementIds: string[] | null) => (
                    <FormPartialPayeeQboMapping
                      disabled={(engagementIds || [])?.length > 1}
                    />
                  )}
                </WSForm.Value>

                <ErrorAlert
                  error={createPayeeMeta.error}
                  context="CreatePayee"
                />

                <WSActions
                  mt="XL"
                  alignment="fill"
                  buttons={[
                    {
                      label: "Add contractor",
                      kind: "Primary",
                      type: "submit",
                      loading: createPayeeMeta.isLoading
                    },
                    {
                      label: "Cancel",
                      kind: "Secondary",
                      type: "button",
                      onClick() {
                        onClose();
                      }
                    }
                  ]}
                />
              </WSList>
            </WSForm>
          );
        }}
      </WSQueries>
    </WSModal>
  );
};
